import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Checkbox, ChevronIcon, CloseIcon, ErrorState, ExternalIcon, LoadingState, SearchIcon, Select, useConfirm, useToast } from "@signet/ui";
import { CreatorAvatar, useCreatorPhotos, useMe, type CreatorPhotos } from "@signet/web-kit";
import { describeTarget, hoursText, MAX_COUNT, MAX_HOURS, sameTarget, type CountPeriod, type TimePeriod } from "../shared/targets.ts";
import { dayLabel, melbourneDay } from "../shared/week.ts";
import { addAccount, BASE, cbKeys, clearSetup, count, fetchSetup, removeAccount, roomUrl, saveSetup, type SetupCreator, type Target } from "./api.ts";
import { CbPage } from "./frame.tsx";
import { ChaturbateMark } from "./logo.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   Creator Setup (Jay, 2026-11-05): every creator in Creators, so one added
   there shows here straight away. A line is the creator on the left and
   what they are expected to do on the right (hours, number of streams),
   then their average viewers: the most viewers in each stream, averaged.
   Clicking it opens their settings: Chaturbate usernames, the target and the
   days they are scheduled to stream. Creators with nothing set up sit in a
   folded list under the rest until something is added to them.
   ────────────────────────────────────────────────────────────────────────── */

const COUNT_OPTIONS: { value: CountPeriod; label: string }[] = [
  { value: "day", label: "per day" },
  { value: "week", label: "per week" },
  { value: "month", label: "per month" },
];
const TIME_OPTIONS: { value: TimePeriod; label: string }[] = [
  { value: "stream", label: "per stream" },
  { value: "day", label: "per day" },
  { value: "week", label: "per week" },
  { value: "month", label: "per month" },
];
const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const PER: Record<CountPeriod | TimePeriod, string> = { stream: "stream", day: "day", week: "week", month: "month" };

const isSetUp = (c: SetupCreator) => c.usernames.length > 0 || c.target !== null;

export function CreatorSetupPage() {
  const setup = useQuery({ queryKey: cbKeys.setup, queryFn: fetchSetup });
  const photos = useCreatorPhotos(`${BASE}/setup`);
  const [find, setFind] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [showRest, setShowRest] = useState(false);
  const words = find.trim().toUpperCase();
  const creators = (setup.data?.creators ?? []).filter((c) => !words || c.creator.includes(words) || c.usernames.some((u) => u.username.toUpperCase().includes(words)));
  const done = creators.filter(isSetUp);
  const rest = creators.filter((c) => !isSetUp(c));
  // Searching opens the folded list, so a creator who isn't set up yet can be found.
  const restOpen = showRest || !!words;

  return (
    <CbPage tab="Creator Setup" intro="Every creator in Creators, with what they're expected to stream. Click a creator for their usernames, target and scheduled days.">
      <div className="cb-search search-box">
        <SearchIcon size={14} />
        <input value={find} onChange={(e) => setFind(e.target.value)} placeholder="Search creators" aria-label="Search creators" autoComplete="off" spellCheck={false} />
      </div>

      {setup.isLoading ? (
        <LoadingState />
      ) : setup.isError || !setup.data ? (
        <ErrorState title="Couldn't load Creator Setup" onRetry={() => setup.refetch()} />
      ) : (
        <>
          <section className="panel">
            <div className="panel-head">
              <h2>
                Set up <span className="cb-faint">{done.length}</span>
              </h2>
            </div>
            <div className="panel-body flush">
              {done.length ? (
                done.map((c) => <SetupLine key={c.creator} creator={c} everyone={setup.data.target} photos={photos} open={open === c.creator} onToggle={() => setOpen(open === c.creator ? null : c.creator)} />)
              ) : (
                <p className="cb-quiet">{words ? "No creator set up matches that." : "Nobody is set up yet. Open a creator below and add their Chaturbate username."}</p>
              )}
            </div>
          </section>

          <section className="panel">
            <button type="button" className="cb-fold" aria-expanded={restOpen} onClick={() => setShowRest(!showRest)}>
              <ChevronIcon size={14} direction={restOpen ? "down" : "right"} />
              <h2>
                Not set up <span className="cb-faint">{rest.length}</span>
              </h2>
            </button>
            {restOpen ? (
              <div className="panel-body flush">
                {rest.length ? (
                  rest.map((c) => <SetupLine key={c.creator} creator={c} everyone={setup.data.target} photos={photos} open={open === c.creator} onToggle={() => setOpen(open === c.creator ? null : c.creator)} />)
                ) : (
                  <p className="cb-quiet">{words ? "No creator matches that." : "Every creator is set up."}</p>
                )}
              </div>
            ) : null}
          </section>
        </>
      )}
    </CbPage>
  );
}

function SetupLine({ creator, everyone, photos, open, onToggle }: { creator: SetupCreator; everyone: Target; photos: CreatorPhotos; open: boolean; onToggle: () => void }) {
  const target = creator.target ?? everyone;
  const setUp = isSetUp(creator);
  return (
    <div className={`cb-setup${open ? " open" : ""}`}>
      <button type="button" className="cb-setup-line" aria-expanded={open} onClick={onToggle}>
        <span className="cb-who">
          <CreatorAvatar name={creator.creator} src={photos(creator.creator)} size={32} />
          <span className="cb-who-text">
            <strong>{creator.creator}</strong>
            <small>{creator.usernames.length ? creator.usernames.map((u) => u.username).join(", ") : "No Chaturbate username"}</small>
          </span>
        </span>
        {setUp ? (
          <>
            <span className="cb-expect">
              <strong>{target.hours ? hoursText(target.hours) : "Any"}</strong>
              <small>hours per {PER[target.hoursPer]}</small>
            </span>
            <span className="cb-expect">
              <strong>{target.count}</strong>
              <small>{target.count === 1 ? "stream" : "streams"} per {PER[target.countPer]}</small>
            </span>
            <span className="cb-expect" title={creator.streams ? `The average of the most viewers in each of ${creator.streams} ${creator.streams === 1 ? "stream" : "streams"}` : "No streams tracked yet"}>
              <strong>{creator.avgPeak === null ? "-" : count(creator.avgPeak)}</strong>
              <small>avg viewers</small>
            </span>
          </>
        ) : (
          <span className="cb-expect wide3">
            <small>Not set up</small>
          </span>
        )}
        <ChevronIcon size={14} direction={open ? "up" : "down"} />
      </button>
      {open ? <Settings creator={creator} everyone={everyone} /> : null}
    </div>
  );
}

/** Typed text for the two numbers, so a box can be cleared and retyped without jumping back to 0. */
type Draft = { count: string; countPer: CountPeriod; hours: string; hoursPer: TimePeriod };
const draftOf = (t: Target): Draft => ({ count: String(t.count), countPer: t.countPer, hours: String(t.hours), hoursPer: t.hoursPer });

function targetOf(d: Draft): Target | null {
  const count = Number(d.count);
  const hours = d.hours.trim() === "" ? 0 : Number(d.hours.replace(",", "."));
  if (!Number.isInteger(count) || count < 1 || count > MAX_COUNT[d.countPer]) return null;
  if (!Number.isFinite(hours) || hours < 0 || hours > MAX_HOURS[d.hoursPer]) return null;
  return { count, countPer: d.countPer, hours, hoursPer: d.hoursPer };
}

const sameDays = (a: number[], b: number[]) => a.length === b.length && a.every((d) => b.includes(d));

function Settings({ creator, everyone }: { creator: SetupCreator; everyone: Target }) {
  const { can } = useMe();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { confirm, element } = useConfirm();
  const saved = creator.target ?? everyone;
  const [draft, setDraft] = useState<Draft>(draftOf(saved));
  const [days, setDays] = useState<number[]>(creator.days);
  const [username, setUsername] = useState("");
  useEffect(() => {
    setDraft(draftOf(creator.target ?? everyone));
    setDays(creator.days);
  }, [creator, everyone]);

  const target = targetOf(draft);
  const editable = can("cbstats.target:edit");
  const changed = !!target && (!creator.target || !sameTarget(target, saved) || !sameDays(days, creator.days));
  const refresh = () => void queryClient.invalidateQueries({ queryKey: cbKeys.all });

  const save = useMutation({
    mutationFn: () => saveSetup(creator.creator, { target: target!, days }),
    onSuccess: () => {
      refresh();
      toast.success("Saved", `${creator.creator}: ${describeTarget(target!)}.`);
    },
    onError: (error: Error) => toast.error("Couldn't save", error.message),
  });
  const reset = useMutation({
    mutationFn: () => clearSetup(creator.creator),
    onSuccess: () => {
      refresh();
      toast.success("Back to the default", `${creator.creator}: ${describeTarget(everyone)}, no days scheduled.`);
    },
    onError: (error: Error) => toast.error("Couldn't reset", error.message),
  });
  const add = useMutation({
    mutationFn: () => addAccount({ creator: creator.creator, username }),
    onSuccess: ({ account }) => {
      setUsername("");
      refresh();
      toast.success("Added", `${account.username} is tracked for ${account.creator} from now on.`);
    },
    onError: (error: Error) => toast.error("Couldn't add", error.message),
  });
  const remove = useMutation({
    mutationFn: (id: string) => removeAccount(id),
    onSuccess: () => refresh(),
    onError: (error: Error) => toast.error("Couldn't remove", error.message),
  });

  return (
    <div className="cb-settings">
      <section>
        <h3>Chaturbate usernames</h3>
        {creator.usernames.length ? (
          <div className="cb-names">
            {creator.usernames.map((u) => (
              <span key={u.id} className="cb-name">
                <ChaturbateMark size={13} />
                <a href={roomUrl(u.username)} target="_blank" rel="noreferrer">
                  {u.username} <ExternalIcon size={11} />
                </a>
                <small>{u.lastLive ? `Last live ${dayLabel(melbourneDay(u.lastLive))}` : `Tracked since ${dayLabel(melbourneDay(u.addedAt))}`}</small>
                {can("cbstats.account:delete") ? (
                  <button
                    type="button"
                    className="icon-button ghost small"
                    aria-label={`Stop tracking ${u.username}`}
                    onClick={async () => {
                      if (await confirm({ title: `Stop tracking ${u.username}?`, body: `It comes off ${creator.creator}. Streams already tracked are kept.`, confirmLabel: "Stop tracking", danger: true })) remove.mutate(u.id);
                    }}
                  >
                    <CloseIcon size={12} />
                  </button>
                ) : null}
              </span>
            ))}
          </div>
        ) : (
          <p className="cb-faint">None yet. Streams are tracked from the moment one is added; history before that isn't available.</p>
        )}
        {can("cbstats.account:create") ? (
          <form
            className="cb-name-add"
            onSubmit={(event) => {
              event.preventDefault();
              if (username.trim().length > 1 && !add.isPending) add.mutate();
            }}
          >
            <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username or room link" aria-label={`Add a Chaturbate username for ${creator.creator}`} autoComplete="off" spellCheck={false} />
            <button type="submit" className="secondary-button compact" disabled={username.trim().length < 2 || add.isPending}>
              Add
            </button>
          </form>
        ) : null}
      </section>

      <section>
        <h3>Target</h3>
        <div className="cb-target-line">
          <input className="cb-target-num" inputMode="numeric" disabled={!editable} aria-label={`${creator.creator}: streams`} value={draft.count} onChange={(e) => setDraft({ ...draft, count: e.target.value.replace(/[^\d]/g, "") })} />
          <span>{draft.count === "1" ? "stream" : "streams"}</span>
          <Select label={`${creator.creator}: streams per`} className="cb-period" compact disabled={!editable} value={draft.countPer} options={COUNT_OPTIONS} onChange={(v) => v && setDraft({ ...draft, countPer: v })} />
          <span className="cb-target-gap">at least</span>
          <input className="cb-target-num" inputMode="decimal" disabled={!editable} aria-label={`${creator.creator}: hours`} value={draft.hours} onChange={(e) => setDraft({ ...draft, hours: e.target.value.replace(/[^\d.,]/g, "") })} />
          <span>hours</span>
          <Select label={`${creator.creator}: hours per`} className="cb-period" compact disabled={!editable} value={draft.hoursPer} options={TIME_OPTIONS} onChange={(v) => v && setDraft({ ...draft, hoursPer: v })} />
        </div>
        <p className={target ? "cb-faint" : "cb-bad"}>
          {target
            ? `${describeTarget(target)}${creator.target ? "" : " (the default)"}`
            : `Streams 1 to ${MAX_COUNT[draft.countPer]} per ${PER[draft.countPer]}, hours 0 to ${MAX_HOURS[draft.hoursPer]} per ${PER[draft.hoursPer]}.`}
        </p>
      </section>

      <section>
        <h3>Scheduled days</h3>
        <div className="cb-days-pick">
          {DAY_NAMES.map((name, d) => (
            <label key={name} className={`cb-day-pick${days.includes(d) ? " on" : ""}`}>
              <Checkbox checked={days.includes(d)} disabled={!editable} label={`${creator.creator} streams on ${name}`} onChange={(on) => setDays(on ? [...days, d].sort() : days.filter((x) => x !== d))} />
              <span>{name}</span>
            </label>
          ))}
        </div>
        <p className="cb-faint">{days.length ? `Expected on ${days.map((d) => DAY_NAMES[d]).join(", ")}. A scheduled day without a stream shows as missed on Reporting.` : "No days scheduled."}</p>
      </section>

      {editable ? (
        <div className="cb-settings-foot">
          {creator.target ? (
            <button type="button" className="text-button" disabled={reset.isPending} onClick={() => reset.mutate()}>
              Use the default
            </button>
          ) : null}
          <button type="button" className="button brass" disabled={!changed || save.isPending} onClick={() => save.mutate()}>
            Save
          </button>
        </div>
      ) : null}
      {element}
    </div>
  );
}
